<script lang="ts" setup>
import {useApi} from '@/ts/composables/stateful/use-api';
import {useRoute, useRouter, type RouteLocationRaw} from 'vue-router';
import {computed, nextTick, ref, watch, watchEffect} from 'vue';
import {ReccoChallengeSessionStateDTO, ReccoChallengeStateDTO, type ChallengeSessionDTO} from '@/ts/types/dto/recco/challenge';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {ReccoRecommendationTypeDTO} from '@/ts/types/dto/recco/recommendation.dto';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {ContentPath, pagesPath} from '@/ts/router/pages-path';
import {useChallengeDetailData} from '@/ts/composables/stateful/use-challenge-detail-data';
import LoadingError from '@/vue/templates/loading-error.vue';
import ReccoVideoComponent from '@/vue/molecules/recco/recco-video-component.vue';
import type {ReccoVideoDTO} from '@/ts/types/dto/recco/video.dto';
import ReccoAudioComponent from '@/vue/molecules/recco/recco-audio-component.vue';
import type {ReccoAudioDTO} from '@/ts/types/dto/recco/audio.dto';
import ReccoArticleComponent from '@/vue/molecules/recco/recco-article-component.vue';
import type {ReccoArticleDTO} from '@/ts/types/dto/recco/article.dto';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import TableOfContentUi from '@/vue/molecules/table-of-content-ui.vue';
import {TableOfContentItemState, type TableOfContentItem} from '@/ts/types/component/table-of-content.type';
import {sessionStorage} from '@/ts/instances/session-storage';
import {isChallengeExpiredPendingAck} from '@/ts/utils/pure-functions';
import SessionUnlocks from '@/vue/molecules/session-summary/session-unlocks.vue';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';
import {useSnackbarStore} from '@/ts/store/snackbar-store';

const api = useApi();
const $q = useQuasar();
const {t: $t} = useI18n();
const snackbarStore = useSnackbarStore();
const route = useRoute();
const router = useRouter();

const taskList = ref<HTMLElement>();
const nextUpCard = ref<HTMLElement>();
const shouldScrollToCooldownCard = ref<boolean>(false);

// The challenge this session belongs to — where the page sends anyone who cannot open the session.
const challengeLink = computed((): RouteLocationRaw => {
  return {
    name: ContentPath.challenge,
    params: {
      id: route.params.id,
    },
  };
});

const {
  loading,
    initialLoading,
    session,
    challenge,
    error,
    fetchChallenge,
} = useChallengeDetailData(Number(route.params.id), () => route.params.sessionId as string);

// The raw session requested in the URL (with its lock state), resolved from the loaded challenge.
const requestedSession = computed((): ChallengeSessionDTO | null =>
  (challenge.value?.sessions ?? []).find(sessionItem => sessionItem.id.toString() === route.params.sessionId) ?? null);

// Sessions are only viewable while the challenge is actively in progress. A completed/failed/
// withdrawn (or never-joined) challenge behaves like NOT_JOINED — the user must re-activate it —
// so its sessions must not be reachable by URL (IPD-2784).
const isChallengeActive = computed((): boolean =>
  challenge.value?.state === ReccoChallengeStateDTO.IN_PROGRESS);

// Accessible = active challenge + a real, non-locked session. Locked/future sessions, cross-challenge
// ids, and sessions of inactive challenges are all reachable by URL manipulation but must not render.
// Mirrors the intervention-session guard (IPD-2751).
const isSessionAccessible = computed((): boolean =>
  isChallengeActive.value &&
  Boolean(requestedSession.value) &&
  requestedSession.value!.state !== ReccoChallengeSessionStateDTO.LOCKED);

// Runs once the challenge has loaded; ordering matters (unknown → 404, inaccessible → landing).
watchEffect(() => {
  if (!challenge.value) {
    return; // challenge still loading — nothing to validate yet
  }
  if (!requestedSession.value) {
    // Unknown session id (not part of this challenge) → error page. Also resolves the infinite
    // loader, since the composable never stops loading when no session matches.
    void router.replace(pagesPath.main.notFound);
    return;
  }
  if (!isSessionAccessible.value) {
    // The session exists but cannot be opened — the challenge is no longer in progress, or the
    // session is still locked. Land on the challenge instead of an error page: this is also the
    // path a back navigation takes after finishing the challenge, and `replace` means that history
    // entry becomes the challenge rather than a dead end.
    void router.replace(challengeLink.value);
  }
});

const toc = computed<TableOfContentItem[]>(() => {
  return (session.value?.tasks ?? []).map<TableOfContentItem>((item, index) => {
    let state = item.state === ReccoChallengeSessionStateDTO.COMPLETED ? TableOfContentItemState.Completed : TableOfContentItemState.Locked;
    if ((session.value?.visibleTasks.length ?? 0) - 1 === index) {
      state = TableOfContentItemState.Current;
    }

    return {
      index,
      state,
      title: item.recommendation.headline,
      link: `#${item.userTaskId}`,
      id: item.userTaskId,
    };
  });
});

const shouldMoveToLastTaskAfterFinishingLoading = ref<boolean>(false);

const isLastSession = computed((): boolean => {
  // Check if this is the last session by comparing index with total sessions
  if (!session.value || !challenge.value) {
    return false;
  }
  const totalSessions = challenge.value.sessions.length;
  return session.value.indexOfSession === totalSessions - 1;
});

const isLastTaskInSession = computed((): boolean => {
  return session.value?.totalNumberOfTasks === session.value?.visibleTasks.length;
});

const nextSession = computed((): ChallengeSessionDTO | null => {
  if (!session.value || !challenge.value) {
    return null;
  }
  const nextIndex = session.value.indexOfSession + 1;
  return nextIndex < challenge.value.sessions.length
    ? challenge.value.sessions[nextIndex]
    : null;
});

const hasNextSessionCooldown = computed((): boolean => {
  return Boolean(nextSession.value?.timeUntilUnlockMillis);
});

const isSessionCompleted = computed((): boolean => {
  return session.value?.state === ReccoChallengeSessionStateDTO.COMPLETED;
});

const showCooldownCard = computed((): boolean => {
  return isSessionCompleted.value && hasNextSessionCooldown.value && nextSession.value !== null;
});

// Next session is available to continue when it's IN_PROGRESS (unlocked by BE after cooldown)
const isNextSessionInProgress = computed((): boolean => {
  return nextSession.value?.state === ReccoChallengeSessionStateDTO.IN_PROGRESS;
});

const nextSessionLink = computed((): RouteLocationRaw | undefined => {
  if (!nextSession.value || !challenge.value) {
    // eslint-disable-next-line no-undefined
    return undefined;
  }
  return {
    name: ContentPath.challengeSession,
    params: {
      id: challenge.value.id,
      sessionId: nextSession.value.id,
    },
  };
});

const showDoneButton = (taskState: ReccoChallengeSessionStateDTO, index: number): boolean => {
  const isCompleted = taskState === ReccoChallengeSessionStateDTO.COMPLETED;
  const isLastTask = session.value ? index === session.value.totalNumberOfTasks - 1 : false;
  // Show Done when: completed last task AND (cooldown card is showing OR it's the last session OR next session is not yet in progress)
  return isCompleted && isLastTask && (showCooldownCard.value || isLastSession.value || !isNextSessionInProgress.value);
};

const showContinueButton = (taskState: ReccoChallengeSessionStateDTO, index: number): boolean => {
  const isCompleted = taskState === ReccoChallengeSessionStateDTO.COMPLETED;
  const isLastTask = session.value ? index === session.value.totalNumberOfTasks - 1 : false;
  // Show Continue when: completed last task AND NOT last session AND next session is IN_PROGRESS (unlocked)
  return isCompleted && isLastTask && !isLastSession.value && isNextSessionInProgress.value;
};

const navigateToLanding = (): void => {
  router.push(challengeLink.value).catch((err: unknown) => {
    $q.notify({type: 'negative', message: extractError(err, $t)});
  });
};

const onClickDone = async(taskId: UuidDTO): Promise<void> => {
  try {
    loading.value = true;
    const wasLastTaskInSession = isLastTaskInSession.value;
    const wasLastSession = isLastSession.value;

    await api.content.flagChallengeTaskAsDone(taskId as string);

    // Check if this completes the entire challenge (last task of last session) and has a badge
    if (wasLastTaskInSession && wasLastSession && challenge.value?.badge) {
      sessionStorage.badgeEarned = challenge.value.badge;
    }

    // Confirmed only short of the challenge's own end (IPD-3328): the last task completes the whole
    // challenge, which the landing marks for itself — with the badge modal when there is one.
    if (!(wasLastSession && wasLastTaskInSession)) {
      snackbarStore.showToast($t('snackbar_task_completed'));
    }

    // If this is the last session, navigate back immediately
    if (wasLastSession && wasLastTaskInSession) {
      navigateToLanding();
      return;
    }

    // Fetch updated data to get cooldown info
    await fetchChallenge();

    // After fetching, check if we should stay (cooldown) or redirect
    if (wasLastTaskInSession && !hasNextSessionCooldown.value) {
      // No cooldown on next session, redirect to challenge page
      navigateToLanding();
    } else if (wasLastTaskInSession) {
      // Cooldown card will show, set flag to scroll when it renders
      shouldScrollToCooldownCard.value = true;
    }

    if (!wasLastTaskInSession) {
      shouldMoveToLastTaskAfterFinishingLoading.value = true;
    }
  } catch (err) {
    $q.notify({type: 'negative', message: extractError(err, $t)});
  } finally {
    loading.value = false;
  }
};

watchEffect(() => {
  if (!loading.value) {
    void (async(): Promise<void> => {
      const allListElements = taskList.value?.getElementsByTagName('li');
      if (shouldMoveToLastTaskAfterFinishingLoading.value && allListElements && allListElements.length > 0) {
        await nextTick();
        allListElements[allListElements.length - 1].scrollIntoView({behavior: 'smooth', block: 'nearest'});
      }
    })();
  }
});

// If the challenge expired while the user had this session open, surface it on the landing.
watch(challenge, (value) => {
  if (isChallengeExpiredPendingAck(value)) {
    sessionStorage.challengeExpired = true;
    navigateToLanding();
  }
});

watch(showCooldownCard, async(newValue) => {
  if (newValue && shouldScrollToCooldownCard.value) {
    shouldScrollToCooldownCard.value = false;
    await nextTick();
    nextUpCard.value?.scrollIntoView({behavior: 'smooth', block: 'center'});
  }
});

</script>

<template>
  <loading-error id="challenge-session-page" :error="error" :loading="initialLoading" type="page">
    <div v-if="session && !initialLoading && isSessionAccessible" class="content-wrapper">
      <div class="header">
        <table-of-content-ui
          :title="session.name"
          :back-link="challengeLink"
          :back-link-label="session.challengeHeadline"
          :completed-percent="session.pctCompletionProgress"
          :items="toc"
          :item-counter-label-key="'recco_challenge_task_num'"
        />
      </div>

      <div class="main-content">
        <ul ref="taskList" class="task-list">
          <li
            v-for="(task, index) in session.visibleTasks"
            :id="task.task.userTaskId"
            :key="task.task.userTaskId"
            class="task-list-item"
          >
            <h2>
              <span class="text-caption">
                {{ $t('recco_challenge_task_num_of', {n: index + 1, total: session.totalNumberOfTasks}) }}
              </span>
            </h2>

            <recco-video-component
              v-if="task.task.recommendation.type === ReccoRecommendationTypeDTO.Video"
              :video-recommendation="task.recommendationPayload as ReccoVideoDTO"
            />

            <recco-audio-component
              v-if="task.task.recommendation.type === ReccoRecommendationTypeDTO.Audio"
              :audio-recommendation="task.recommendationPayload as ReccoAudioDTO"
            />

            <recco-article-component
              v-if="task.task.recommendation.type === ReccoRecommendationTypeDTO.Article"
              :article-recommendation="task.recommendationPayload as ReccoArticleDTO"
            />

            <div class="button-bar">
              <balm-btn
                v-if="task.task.state === ReccoChallengeSessionStateDTO.IN_PROGRESS"
                :label="$t('cta_complete')"
                :loading="loading"
                @click="onClickDone(task.task.userTaskId)"
              />

              <svg-illustration v-else-if="index !== session.totalNumberOfTasks - 1" name="check.svg"/>

              <balm-btn
                v-if="showDoneButton(task.task.state, index)"
                :label="$t('done')"
                :to="challengeLink"
              />

              <balm-btn
                v-if="showContinueButton(task.task.state, index)"
                :label="$t('continue')"
                :to="nextSessionLink"
              />
            </div>
          </li>
        </ul>

        <div v-if="showCooldownCard" ref="nextUpCard" class="next-up-card">
          <div class="next-up-content">
            <div class="next-up-text">
              <h3 class="next-up-title">
                {{ $t('next_up', {page: nextSession?.name || ''}) }}
              </h3>
              <p v-if="nextSession?.description" v-safe-html="nextSession.description" class="next-up-description"/>
            </div>
            <div class="next-up-action">
              <session-unlocks :session="nextSession"/>
            </div>
          </div>
          <div class="next-up-footer">
            <balm-btn
              :label="$t('back_to_challenge')"
              :to="challengeLink"
              class="back-to-challenge-btn"
              theme="outline"
            />
          </div>
        </div>
      </div>
    </div>
  </loading-error>
</template>

<style lang="scss" scoped>
.content-wrapper {
  padding: var(--Spacing-spacing-5);
  @include screen-lg {
    display: flex;
  }
}

.header {
  margin-bottom: var(--Spacing-spacing-4);
  @include screen-lg {
    @include column-width(2);
    min-width: 250px;
    padding-right: var(--Spacing-spacing-1);
  }
  @include screen-xl {
    padding-right: var(--Spacing-spacing-3);
  }
}

.main-content {
  @include page-alignment(1032px);
  padding: 0;
  margin: 0 auto;

  @include screen-lg {
    padding: 0 var(--Spacing-spacing-5);
  }
}

.task-list {
  margin: 0;
  list-style: none;
  width: 100%;
  padding: 0;
}

.task-list-item {
  @include q-card-white;
  padding: var(--Spacing-spacing-4);
  margin-bottom: var(--Spacing-spacing-10);

  &:last-child {
    margin-bottom: var(--Spacing-spacing-4);
  }
}

.button-bar {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
}

.next-up-card {
  @include q-card-blue;
  padding: var(--Spacing-spacing-5);

  @include screen-lg {
    padding: var(--Spacing-spacing-6);
  }
}

.next-up-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: var(--Spacing-spacing-6);

  @media screen and (max-width: 639px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--Spacing-spacing-4);
  }
}

.next-up-text {
  flex: 1;
}

.next-up-title {
  @include font-h3-bold;
  margin: 0 0 var(--Spacing-spacing-2) 0;
  color: var(--Text-text-90);
}

.next-up-description {
  @include font-body;
  margin: 0;
  color: var(--Text-text-70);
}

.next-up-action {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;

  @media screen and (max-width: 639px) {
    justify-content: flex-start;
  }
}

.next-up-footer {
  margin-top: var(--Spacing-spacing-4);
  display: flex;
  justify-content: flex-start;
}

.back-to-challenge-btn {
  color: var(--Text-text-90) !important;
}
</style>
